import {SidebarSection} from '@github-ui/pacer/SidebarNavigation'
import {useLocation} from '@github-ui/react-router'
import {Fragment} from 'react'
import type React from 'react'

import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {useSSOChangeDetection} from '../../hooks/use-sso-change-detection'
import {useTasks} from '../../hooks/useTasks'
import {generateTaskPathForRepo} from '../../utils/generate-task-path'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import {TaskActionProviders} from '../task-list/TaskActionProviders'
import {SidebarTaskListItem} from './SidebarTaskListItem'
import {getTaskSecondaryText, groupTasksByUpdatedAt, sortTasksByUpdatedAtDesc, TaskBucketHeader} from './task-buckets'

const PAGE_SIZE = 5

export function SidebarTaskList({
  viewAllLink,
  showTitle,
  trailingAction,
  onTaskClick,
}: {
  viewAllLink?: React.ReactNode
  showTitle?: boolean
  trailingAction?: React.ReactNode
  onTaskClick?: () => void
}) {
  return (
    <ConsecutiveCAPI401sProvider>
      <TaskActionProviders>
        <ArchiveErrorBanner />
        <SidebarTaskListBody
          viewAllLink={viewAllLink}
          showTitle={showTitle}
          trailingAction={trailingAction}
          onTaskClick={onTaskClick}
        />
      </TaskActionProviders>
    </ConsecutiveCAPI401sProvider>
  )
}

function SidebarTaskListBody({
  viewAllLink,
  showTitle,
  trailingAction,
  onTaskClick,
}: {
  viewAllLink?: React.ReactNode
  showTitle?: boolean
  trailingAction?: React.ReactNode
  onTaskClick?: () => void
}) {
  const location = useLocation()

  const {tasks, isLoading, dataUpdatedAt} = useTasks({
    taskFilter: 'active',
    pageSize: PAGE_SIZE,
    includeCounts: true,
  })
  useSSOChangeDetection()

  const visibleTasks = sortTasksByUpdatedAtDesc(tasks).slice(0, PAGE_SIZE)
  const groupedVisibleTasks = groupTasksByUpdatedAt(visibleTasks)
  const isEmpty = visibleTasks.length === 0

  return (
    <>
      <SidebarSection
        title={showTitle ? 'Agent sessions' : undefined}
        isEmpty={isEmpty}
        isLoading={isLoading}
        trailingAction={trailingAction}
      >
        {groupedVisibleTasks.map(bucket => (
          <Fragment key={bucket.label}>
            {groupedVisibleTasks.length > 1 && <TaskBucketHeader label={bucket.label} />}
            {bucket.tasks.map(task => {
              const href = generateTaskPathForRepo({
                repositoryNwo: task.nwo,
                sessionId: task.latest_session_id,
                taskId: task.id,
              })
              const isSelected = location.pathname === href.split('?')[0]

              const nwo = task.nwo
              const [owner, repo] = nwo ? nwo.split('/') : [undefined, undefined]

              return (
                <SidebarTaskListItem
                  key={task.clientGeneratedId ?? task.id ?? task.resource_id}
                  task={task}
                  href={href}
                  isSelected={isSelected}
                  reloadDocument={!!task.nwo}
                  secondaryText={getTaskSecondaryText(task)}
                  onTaskClick={onTaskClick}
                  repoOwner={owner}
                  repoName={repo}
                  dataUpdatedAt={dataUpdatedAt}
                  currentUserIsOwner
                />
              )
            })}
          </Fragment>
        ))}
      </SidebarSection>
      {!isEmpty && viewAllLink}
    </>
  )
}
